<template>
  <q-card style="width: 100vw; height: 100vh">
    <q-toolbar
      class="bg-primary text-secondary fixed issue-details-toolbar"
      :class="{ 'safe-area-header': needInset.all }"
      style="z-index: 9999"
    >
      <q-toolbar-title class="text-center q-pl-md">
        <span class="sw-title-secondary"
          >{{ $t('issueDetails.sending') }}{{ issueId }}
        </span>
      </q-toolbar-title>
      <q-btn
        class="absolute-left"
        flat
        icon="close"
        @click="$emit('closeModal')"
      />
    </q-toolbar>

    <div class="column items-center">
      <div style="min-width: 350px">
        <div class="text-subtitle1 q-pl-md" style="margin-top: 70px">
          {{ $t('issueDetails.sendByEmail.recipients') }}
        </div>
        <div class="q-mt-md q-mx-md input-email-container">
          <sw-email-select
            v-model="selectedUsers"
            :options="userSelectOptions"
            :placeholder="$t('common.emailAddress')"
            :error-message="$t('issueDetails.sendByEmail.recipientsWrong')"
          />
        </div>
        <template v-if="suggestProvidersInSecurity && allProviders.length > 0">
          <div class="text-subtitle1 q-pl-md q-mb-sm q-mt-xl">
            {{ $t('common.suggestions') }}
          </div>
          <div class="q-mt-md q-pl-md">
            <q-list class="providers-list">
              <q-item
                v-for="provider in allProviders"
                :key="provider.email"
                clickable
                v-ripple
              >
                <q-item-section avatar>
                  <q-checkbox
                    v-model="selectedProviders"
                    :val="provider.email"
                  />
                </q-item-section>
                <q-item-section>
                  <q-item-label
                    >{{ provider.firstName }}
                    {{ provider.lastName }}</q-item-label
                  >
                  <q-item-label caption>{{ provider.email }}</q-item-label>
                </q-item-section>
              </q-item>
            </q-list>
          </div>
        </template>
        <div class="text-subtitle1 q-pl-md q-mb-sm">
          {{ $t('issueDetails.sendByEmail.body') }} ({{
            $t('common.optional')
          }})
        </div>
        <div
          class="q-pr-md q-pl-md"
          style="max-width: 350px; margin-bottom: 110px"
        >
          <q-input v-model="message" outlined autogrow />
        </div>
        <q-page-sticky position="bottom" :offset="[18, 0]">
          <q-btn
            style="min-width: 318px"
            unelevated
            no-caps
            color="primary"
            :label="$t('common.send')"
            :loading="onSent"
            icon="send"
            @click="sendIssueByEmail"
            :disabled="isSubmitBtnDisabled"
          />
        </q-page-sticky>
      </div>
    </div>
  </q-card>
</template>

<script>
import { defineComponent, computed, ref, inject, onMounted } from 'vue'
import { Notify } from 'quasar'
import moment from 'moment'
import { useQuasar } from 'quasar'
import { mapGetters, useStore } from 'vuex'
import SwEmailSelect from 'src/components/inputs/sw-email-select.vue'

import { i18n } from 'src/boot/i18n'
const { t } = i18n.global

import * as yup from 'yup'

export default defineComponent({
  name: 'SendIssue',
  components: {
    SwEmailSelect
  },
  props: {
    issueId: {
      type: Number,
      required: true
    }
  },
  emits: ['closeModal'],
  computed: {
    ...mapGetters({
      needInset: 'common/needInset'
    })
  },
  setup(props, { emit }) {
    const { platform } = useQuasar()
    const $store = useStore()
    const $SowellAPI = inject('sowellProxy')
    const message = ref('')
    const onSent = ref(false)
    const allProviders = ref([])
    const selectedProviders = ref([])
    const allUsers = ref([])
    const selectedUsers = ref([])

    const suggestProvidersInSecurity = computed(() => {
      return $store.getters['company/attributes'].suggestProvidersInSecurity
    })

    const userSelectOptions = computed(() => {
      if (!allUsers.value || allUsers.value.length === 0) return []
      return allUsers.value.map((user) => ({
        label: `${user.firstName} ${user.lastName}`,
        value: user.email
      }))
    })

    const emailsToSend = computed(() => {
      // selectedUsers est un tableau d'emails (strings) grâce à option-value="value"
      const all = [...selectedUsers.value, ...selectedProviders.value].filter(
        (email) => email !== '' && email !== null && email !== undefined
      )
      // Remove duplicates
      return Array.from(new Set(all))
    })


    const isSubmitBtnDisabled = computed(() => {
      const rule = yup.string().required().email()
      if (emailsToSend.value.length < 1) return true
      return (
        emailsToSend.value.some((email) => {
          return !rule.isValidSync(email)
        }) || false
      )
    })

    const handleError = (status, error) => {
      if (status) {
        if (
          Number(error.response.status) === 400 ||
          Number(error.response.status) === 422
        ) {
          Notify.create({
            message: t('issueDetails.sendByEmail.sendFailed'),
            color: 'negative',
            type: 'negative',
            position: 'top'
          })
        }
      } else {
        Notify.create({
          message: t('common.error.serverError'),
          color: 'negative',
          type: 'negative',
          position: 'top'
        })
      }
    }

    const sendIssueByEmail = async () => {
      onSent.value = true
      const dateNow = moment()
      const dateFormated = `${dateNow.format('DD/MM/YYYY')} à ${dateNow.format(
        'HH:mm'
      )}`
      try {
        await $SowellAPI.sendIssueExport({
          issue_id: props.issueId,
          message: message.value,
          emails: emailsToSend.value.toString(),
          date_creation: dateFormated
        })
        Notify.create({
          message: t('issueDetails.sendByEmail.sendSuccess'),
          color: 'positive',
          icon: 'check',
          position: 'top'
        })
        emit('closeModal')
        onSent.value = false
      } catch (error) {
        console.error(
          '🚀 ~ file: SendIssue.vue:157 ~ sendIssueByEmail ~ error:',
          error
        )
        onSent.value = false

        if (platform.is.capacitor) {
          const { Network } = await import('Capacitor/@capacitor/network')
          const status = await Network.getStatus()
          handleError(status.connected, error)
          return
        }
        const online = window.navigator.onLine
        handleError(online, error)
      }
    }

    const loadProviders = async () => {
      const response = await $store.dispatch('reporter/loadProviders')
      allProviders.value = response
    }

    const loadAllUsers = async () => {
      const response = await $store.dispatch('reporter/fetchAllUsers')
      allUsers.value = response
    }

    onMounted(() => {
      if (suggestProvidersInSecurity.value) {
        loadProviders()
      }
      loadAllUsers()
    })

    return {
      allProviders,
      selectedProviders,
      suggestProvidersInSecurity,
      isSubmitBtnDisabled,
      message,
      onSent,
      allUsers,
      selectedUsers,
      userSelectOptions,
      sendIssueByEmail
    }
  }
})
</script>

<style scoped lang="sass">
.providers-list
  max-height: 120px
  overflow-y: auto
  background-color: #f9f9f9

.input-email-container
  max-width: 322px !important
</style>
